import { useState, type ReactElement } from 'react';
import { useLocation, useRoute } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  CalendarDays,
  ChevronRight,
  Clock3,
  GraduationCap,
  Loader2,
  TrendingUp,
  Users,
} from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { getPublicRankingSample, getTeacherRanking } from '../../lib/admin-api';
import { DEMO_TEACHER_BASE, teacherHref } from '../../lib/demo-teacher';
import { cn } from '../../lib/utils';

function useClassStats(days: number, classId?: number | null, demo = false) {
  return useQuery({
    queryKey: demo ? ['public', 'teacher-demo', 'classes', days, classId] : ['teacher', 'classes', days, classId],
    queryFn: () => (demo ? getPublicRankingSample(days, classId) : getTeacherRanking(days, classId)),
    staleTime: 60 * 1000,
  });
}

export function TeacherClasses() {
  const [, setLocation] = useLocation();
  const { data, isLoading } = useClassStats(0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="text-3xl font-bold text-gray-900">クラス別成績</h1>
          <p className="mt-2 text-sm text-gray-600">集計を確認する担当クラスを選択します。</p>
        </header>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            読み込み中...
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {(data?.classes ?? []).map((educatorClass) => (
              <button
                key={educatorClass.id}
                type="button"
                onClick={() => setLocation(`/teacher/classes/${educatorClass.id}`)}
                className="group min-h-[150px] rounded-lg border border-gray-200 bg-white p-5 text-left transition-colors hover:border-cyan-600 hover:bg-cyan-50 focus:outline-none focus:ring-2 focus:ring-cyan-700"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="grid h-10 w-10 place-items-center rounded-lg bg-cyan-50 text-cyan-800">
                      <GraduationCap className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-xs font-bold text-cyan-800">担当クラス</div>
                      <div className="mt-1 text-lg font-bold text-gray-900">{educatorClass.name}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {educatorClass.academicYear ? `${educatorClass.academicYear}年度` : '年度未設定'}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="mt-2 h-5 w-5 text-gray-400 group-hover:text-cyan-800" />
                </div>
                <div className="mt-5 text-sm text-gray-600">学生 {educatorClass.studentCount}名</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

type Period = 'week' | 'month' | 'all';

const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
  { value: 'week', label: '今週' },
  { value: 'month', label: '今月' },
  { value: 'all', label: '全期間' },
];

/** 教員画面の公開デモ（/demo/teacher/classes/:classId） */
export function DemoTeacherClassDashboard() {
  return <TeacherClassDashboard demo />;
}

export function TeacherClassDashboard({ demo = false }: { demo?: boolean } = {}) {
  const [, setLocation] = useLocation();
  const [, params] = useRoute(demo ? `${DEMO_TEACHER_BASE}/classes/:classId` : '/teacher/classes/:classId');
  const [period, setPeriod] = useState<Period>('month');
  const classId = Number(params?.classId);
  const days = period === 'week' ? 7 : period === 'month' ? 30 : 0;
  const { data, isLoading } = useClassStats(days, Number.isInteger(classId) ? classId : null, demo);

  if (isLoading) {
    return (
      <AdminLayout demo={demo}>
        <div className="flex items-center justify-center gap-2 py-16 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          読み込み中...
        </div>
      </AdminLayout>
    );
  }

  const className = data?.classes.find((educatorClass) => educatorClass.id === classId)?.name ?? '担当クラス';
  const totalUsageSeconds = (data?.students ?? []).reduce((sum, student) => sum + student.totalUsageSeconds, 0);
  const weeklyTrend = (data?.weeklyScoreTrend ?? []).map((item) => ({
    ...item,
    label: new Date(item.weekStart).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' }),
  }));
  const studentsToReview = [...(data?.students ?? [])]
    .filter((student) => student.interviewCount > 0)
    .sort((a, b) => a.averageScore - b.averageScore || b.interviewCount - a.interviewCount)
    .slice(0, 5);

  return (
    <AdminLayout demo={demo}>
      <div className="space-y-7">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Button variant="ghost" className="mb-2 px-0 text-cyan-800" onClick={() => setLocation(teacherHref(demo, '/teacher'))}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              ダッシュボードへ戻る
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">{className}</h1>
            <p className="mt-2 text-sm text-gray-600">クラス単位の利用状況と成績を確認します。</p>
          </div>

          <div className="inline-flex w-fit rounded-lg border border-gray-200 bg-white p-1" aria-label="集計期間">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setPeriod(option.value)}
                className={cn(
                  'min-w-20 rounded-md px-3 py-2 text-sm font-bold transition-colors',
                  period === option.value ? 'bg-cyan-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </header>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric label="アクティブユーザー" value={`${data?.summary.activeStudentCount ?? 0}名`} icon={<Users />} />
          <Metric label="ロールプレイ回数" value={`${data?.summary.totalInterviewCount ?? 0}回`} icon={<GraduationCap />} />
          <Metric label="平均点" value={`${Number(data?.summary.averageScore ?? 0).toFixed(1)}点`} icon={<TrendingUp />} />
          <Metric label="推定利用時間" value={formatDuration(totalUsageSeconds)} icon={<Clock3 />} />
        </div>

        <section className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900">平均点の推移</h2>
              <p className="mt-1 text-sm text-gray-500">患者面接の採点結果を1週間ごとに平均しています。</p>
            </div>
            <CalendarDays className="h-5 w-5 text-gray-400" />
          </div>
          <div className="mt-4 flex h-56 items-center justify-center rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-500">
            {weeklyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyTrend} margin={{ top: 12, right: 18, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} width={42} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number | string) => [`${Number(value).toFixed(1)}点`, '週平均']}
                    labelFormatter={(label) => `${label}の週`}
                  />
                  <Line type="linear" dataKey="average" stroke="#0e7490" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : '対象期間の採点結果がありません。'}
          </div>
        </section>

        <div className="grid gap-6 border-t border-gray-200 pt-6 lg:grid-cols-2">
          <section>
            <h2 className="text-xl font-bold text-gray-900">要確認の学生</h2>
            <p className="mt-1 text-sm text-gray-500">対象期間の平均点が低い順に表示します。</p>
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
              {studentsToReview.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {studentsToReview.map((student) => (
                    <button
                      key={student.studentId}
                      type="button"
                      onClick={() => setLocation(teacherHref(demo, `/teacher/students/${student.studentId}`))}
                      className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-cyan-50"
                    >
                      <span>
                        <span className="block font-bold text-gray-900">{student.name}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {student.participantCode ?? '学籍番号未登録'} · 面接{student.interviewCount}回
                        </span>
                      </span>
                      <span className="flex items-center gap-2 font-bold text-gray-900">
                        {student.averageScore.toFixed(1)}点
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="px-5 py-10 text-center text-sm text-gray-500">対象期間の面接がありません。</div>
              )}
            </div>
          </section>
          <section>
            <h2 className="text-xl font-bold text-gray-900">評価カテゴリ別の達成率</h2>
            <p className="mt-1 text-sm text-gray-500">達成率が低いカテゴリから表示します。</p>
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
              {(data?.categoryPerformance ?? []).length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {(data?.categoryPerformance ?? []).slice(0, 6).map((category) => (
                    <div key={category.categoryName} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="font-bold text-gray-900">{category.categoryName}</span>
                        <span className="font-bold text-gray-700">{(category.achievementRate * 100).toFixed(1)}%</span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full bg-cyan-700"
                          style={{ width: `${Math.max(0, Math.min(100, category.achievementRate * 100))}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-5 py-10 text-center text-sm text-gray-500">評価カテゴリの記録がありません。</div>
              )}
            </div>
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}

function Metric({
  label,
  value,
  icon,
  muted = false,
}: {
  label: string;
  value: string;
  icon: ReactElement;
  muted?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3 text-sm font-bold text-gray-600">
        {label}
        <span className="text-cyan-800 [&>svg]:h-5 [&>svg]:w-5">{icon}</span>
      </div>
      <div className={cn('mt-3 text-2xl font-bold', muted ? 'text-gray-400' : 'text-gray-900')}>{value}</div>
    </div>
  );
}

function formatDuration(value: number): string {
  const seconds = Math.max(0, Math.round(value));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}
